import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import { NavStack, NavStackStage, NavStackScreen } from '@/components/custom/route-transition/stack/nav-stack'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'

// The HIERARCHICAL member of the transition playground (see dev-resources.transitions.tsx): a two-level
// nav stack exactly like a section's — level 0 an item list, level 1 the pushed item screen, which hosts
// the INNER lateral pager (dev-resources.transitions.one.item.tsx). One `levels` declaration is the whole
// hierarchy: the engine asks it of the rendered pathname AND the one being navigated to (so pushes are eager
// while pops keep their content), and of a tapped row's target (so the travel starts on the tap's own frame).
export const Route = createFileRoute('/_app/dev-resources/transitions/one')({
  component: RouteComponent,
})

const ITEM_OPEN = /\/transitions\/one\/item(\/|$)/

function ItemList() {
  return (
    <div className="flex flex-col gap-2 p-4">
      <p className="text-muted-foreground text-sm">
        Level 0 — the stack root. A row pushes the item screen, whose tabs are the inner lateral pager.
      </p>
      {Array.from({ length: 3 }, (_, i) => (
        <Link
          key={i}
          to="/dev-resources/transitions/one/item"
          className="hover:bg-accent flex items-center justify-between rounded-md border p-3 text-sm"
        >
          Playground item {i + 1}
          <ChevronRightIcon className="text-muted-foreground size-4" />
        </Link>
      ))}
    </div>
  )
}

function RouteComponent() {
  const navigate = useNavigate()
  // Live previews route through here too, and which one decides what this renders: the INNER pager's preview is
  // of a surface BELOW us, so fall through to the item layout's own gate; the OUTER pager previewing `one` is
  // ABOVE us, and wants the bare list (no stack chrome, no observers).
  const preview = usePreviewMode('/dev-resources/transitions/one')
  if (preview === 'passthrough') return <Outlet />
  if (preview === 'above') return <ItemList />
  return (
    // The playground carries the real thing, eager push included — it is where the stack is driven by hand.
    <NavStack levels={[true, ITEM_OPEN]} onPop={() => void navigate({ to: '/dev-resources/transitions/one' })}>
      {/* On a phone the stack's layers are `position: absolute; inset: 0` — the stage must own a real
          height (the section layouts get theirs from the app shell; this playground sets its own). */}
      <NavStackStage className="flex w-full overflow-hidden max-md:h-[65dvh]" style={{ containerType: 'inline-size' }}>
        <NavStackScreen index={0} title="Playground items">
          <ItemList />
        </NavStackScreen>
        <NavStackScreen index={1} title="Item — nested tabs">
          <Outlet />
        </NavStackScreen>
      </NavStackStage>
    </NavStack>
  )
}
